import React from 'react';
import { common, query } from '@kurtosys/ksys-app-template';
import { IFormatter, IRebaseOptions } from '@kurtosys/types/data/index.js';
import * as changeCase from 'change-case';
import { Text, TextProps } from 'recharts';

import {
	IChartData,
	IXAxisProps,
	IYAxisProps,
	TAxisDomain,
	TChartElement,
	TChartElementBar,
	TChartElementLegend,
	TChartElementXAxis,
	TChartElementYAxis,
	TComposedChartProps,
	TDataKey,
	TTickFormatterFunction,
} from '../../../models/charting/index.js';
import { IBreakpointProps } from '../../../models/IBreakpointProps.js';
import { IGlobalConfiguration } from '../../../models/IGlobalConfiguration.js';
import { QueryStore } from '../../../shared/QueryStore.js';
import { ITableProps } from '../../base/Table/models/index.js';
import { ITableColumn } from '../../base/Table/models/ITableColumn.js';
import { TableLogic } from '../../base/Table/TableLogic.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import { IDisclaimersProps } from '../../overview/Disclaimers/models/index.js';
import { IScatterChartProps } from '../ScatterChart/models/index.js';
import { CustomizedShape } from '../ScatterChart/styledComponents/CustomizedShape.js';

import { IAxisTickOptions } from './models/IAxisTickOptions.js';
import { IDateOptions } from './models/IDateOptions.js';
import { IChartBaseProps } from './models/index.js';
import { ISingleChart } from './models/ISingleChart.js';
import { ISyncTickOptions } from './models/ISyncTickOptions.js';
import { TSingleChartElement } from './models/TSingleChartElement.js';
import CustomSeriesLabel from './styledComponents/CustomSeriesLabel.js';
import LastSeriesLabel from './styledComponents/LastSeriesLabel.js';
import { TickHandler } from './TickHandler.js';

export const KSYS_CHART_ANIMATION_KEY = 'ksysChartIsAnimationActive';

const SIZE = 32;

export function orchestrateElements<T>(
	defaultElements: IChartBaseProps['elements'],
	...configurations: ISingleChart<T>[]
) {
	const response: IChartBaseProps['elements'] = [];
	if (defaultElements) {
		for (const defaultElement of defaultElements) {
			const element = Object.assign({}, defaultElement);
			let override: IOverrideChartElementResponse | undefined;
			for (const configuration of configurations || []) {
				override = overrideChartElement(element, configuration, override);
			}
			if (override && !override.exclude) {
				response.push(element);
			}
		}
	}
	return response;
}

export interface IOverrideChartElementResponse {
	element: TChartElement;
	exclude?: boolean | undefined;
}

function overrideChartElement<T>(
	element: TChartElement,
	chart?: ISingleChart<T>,
	previousOverride?: IOverrideChartElementResponse,
): IOverrideChartElementResponse {
	const response: IOverrideChartElementResponse = {
		element,
		exclude: previousOverride && previousOverride.exclude,
	};

	const elementOverridesKey = changeCase.camelCase(element.type);
	if (chart && chart.elementOverrides) {
		const override = (chart.elementOverrides as any)[elementOverridesKey];
		if (override) {
			response.exclude = !!override.hide;
			if (!response.exclude) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { hide, ...otherProps } = override;
				element.props = common.commonUtils.deepMergeObjects(element.props || {}, otherProps);
			}
		}
	}

	return response;
}

export function orchestrateElement<T>(element: TChartElement, ...overrides: T[]) {
	const response: TChartElement | undefined = Object.assign({}, element);
	response.props = common.commonUtils.deepMergeObjects(element.props || {}, ...overrides);
	return response;
}

export function getResponsiveContainerProps(
	...configurations: ISingleChart<any>[]
): IChartBaseProps['responsiveContainerProps'] {
	const defaultResponsiveContainerProps: IChartBaseProps['responsiveContainerProps'] = {};
	const configurationsToMerge = (configurations || []).map((configuration) => configuration.responsiveContainerProps);
	return common.commonUtils.deepMergeObjects(defaultResponsiveContainerProps, ...configurationsToMerge);
}

export function getComposedChartProps(...configurations: ISingleChart<any>[]): IChartBaseProps['composedChartProps'] {
	if (configurations && configurations.length > 0) {
		const props = configurations[configurations.length - 1];
		const { composedChartProps } = props;
		let { data } = props;
		if (!data) {
			data = composedChartProps && composedChartProps.data;
		}
		const defaultComposedChartProps: IChartBaseProps['composedChartProps'] = {};
		const configurationsToMerge = (configurations || []).map((configuration) => configuration.composedChartProps);
		return common.commonUtils.deepMergeObjects(defaultComposedChartProps, ...configurationsToMerge, { data });
	}
	return {};
}

export function getCoreChartElements(...configurations: ISingleChart<any>[]): IChartBaseProps['elements'] {
	const defaultCoreChartElements: IChartBaseProps['elements'] = [
		{
			type: 'CartesianGrid',
		},
		{
			type: 'XAxis',
		},
		{
			type: 'YAxis',
		},
		{
			type: 'ZAxis',
		},
		{
			type: 'Tooltip',
		},
		{
			type: 'Legend',
		},
	];
	return orchestrateElements(defaultCoreChartElements, ...(configurations || []));
}

export function getSeriesElement(
	defaultSeriesElement: TChartElement,
	...configurations: ISingleChart<any>[]
): TChartElement[] {
	const chartElements: TChartElement[] = [];
	if (configurations && configurations.length > 0) {
		const props = configurations[configurations.length - 1];
		const { seriesProps: fromPropsCollection = [] } = props;
		const seriesColors = getSeriesColors(...configurations);
		const commonConfigurations = configurations.map((configuration) => configuration.commonSeriesProps);
		for (let seriesIndex = 0; seriesIndex < fromPropsCollection.length; seriesIndex++) {
			const fromProps = fromPropsCollection[seriesIndex];
			if (fromProps.hasData) {
				const seriesElement = orchestrateElement(defaultSeriesElement, ...commonConfigurations, fromProps);
				const { props } = seriesElement;
				if (props && !(props as any).fill && seriesColors && seriesColors.length > 0) {
					const color = seriesColors[seriesIndex % seriesColors.length];
					(props as any).fill = color;
					(props as any).colors = seriesColors;
				}
				chartElements.push(seriesElement);
			}
		}
	}
	return chartElements;
}

export function getSeriesColors(...configurations: ISingleChart<any>[]) {
	for (const configuration of [...configurations].reverse()) {
		const { colors } = configuration;
		if (colors) {
			return colors;
		}
	}
	return [];
}

export function getAdditionalChartElements(...configurations: ISingleChart<any>[]) {
	for (const configuration of [...configurations].reverse()) {
		const { additionalElements } = configuration;
		if (additionalElements) {
			return additionalElements;
		}
	}
	return [];
}

export function getChartElements(
	defaultSeriesElement: TChartElement,
	...configurations: ISingleChart<any>[]
): IChartBaseProps['elements'] {
	return [
		...(getCoreChartElements(...configurations) || []),
		...appendReferenceLines(configurations),
		...getSeriesElement(defaultSeriesElement, ...configurations),
		...getAdditionalChartElements(...configurations),
	];
}

export function getMeanTickPivotPoint(composedChartData: TComposedChartProps[], dataKey: TDataKey) {
	const total = composedChartData.reduce((total, current: any) => {
		return total + Number(current[dataKey.toString()]);
	}, 0);
	return Math.round(total / composedChartData.length);
}

export function appendReferenceLines(configurations: (ISingleChart<any> | undefined)[]): TChartElement[] {
	const referenceLines: TChartElement[] = [];
	for (const configuration of [...configurations].reverse()) {
		if (configuration) {
			const { xPivotReferenceLine, yPivotReferenceLine, elementOverrides } = configuration;
			if (elementOverrides) {
				if (elementOverrides.xAxis && xPivotReferenceLine) {
					const pivot = elementOverrides.xAxis.tickPivotPoint;
					if (pivot !== undefined) {
						referenceLines.push({
							props: {
								stroke: 'black',
								x: pivot,
								...xPivotReferenceLine,
							},
							type: 'ReferenceLine',
						});
					}
				}
				if (elementOverrides.yAxis && yPivotReferenceLine) {
					const pivot = elementOverrides.yAxis.tickPivotPoint;
					if (pivot !== undefined) {
						referenceLines.push({
							props: {
								stroke: 'black',
								y: pivot,
								...yPivotReferenceLine,
							},
							type: 'ReferenceLine',
						});
					}
				}
			}
		}
	}

	return referenceLines;
}

export function getChartDisclaimersProps(...configurations: ISingleChart<any>[]): IDisclaimersProps | undefined {
	for (const configuration of [...configurations].reverse()) {
		const { disclaimersProps } = configuration;
		if (disclaimersProps) {
			return disclaimersProps;
		}
	}
	return;
}

export function getChartBaseProps(
	defaultSeriesElement: TChartElement,
	...configurations: (ISingleChart<any> | undefined)[]
): IChartBaseProps {
	const cleanConfigurations: ISingleChart<any>[] = (
		configurations.filter((configuration) => !!configuration) as ISingleChart<any>[]
	).map((configuration) => {
		if (
			configuration &&
			configuration.elementOverrides &&
			configuration.elementOverrides.xAxis &&
			configuration.elementOverrides.yAxis &&
			configuration.composedChartProps &&
			configuration.composedChartProps.data
		) {
			const xAxis = configuration.elementOverrides.xAxis;
			const yAxis = configuration.elementOverrides.yAxis;
			let updatedConfiguration = configuration;
			if (yAxis.useMeanTickPivotPoint && !yAxis.tickPivotPoint && yAxis.dataKey) {
				updatedConfiguration = {
					...configuration,
					elementOverrides: {
						...configuration.elementOverrides,
						yAxis: {
							tickPivotPoint: getMeanTickPivotPoint(configuration.composedChartProps.data, yAxis.dataKey),
							...yAxis,
						},
					},
				};
			}
			if (xAxis.useMeanTickPivotPoint && !xAxis.tickPivotPoint && xAxis.dataKey) {
				updatedConfiguration = {
					...configuration,
					elementOverrides: {
						...updatedConfiguration.elementOverrides,
						xAxis: {
							tickPivotPoint: getMeanTickPivotPoint(configuration.composedChartProps.data, xAxis.dataKey),
							...xAxis,
						},
					},
				};
			}
			return updatedConfiguration;
		}
		return configuration;
	});
	// various functions do the reverse() before using the config so do it once here and pass it where necessary
	const reverseCleanConfigurations = [...cleanConfigurations].reverse();

	seriesHasData(...cleanConfigurations);
	const response: IChartBaseProps = {
		cleanConfigurations,
		// Passed as prop by app
		appKey: reverseCleanConfigurations[0].appKey,
		// Passed as prop by app
		queryStore: reverseCleanConfigurations[0].queryStore,
		responsiveContainerProps: getResponsiveContainerProps(...cleanConfigurations),
		composedChartProps: getComposedChartProps(...cleanConfigurations),
		elements: getChartElements(defaultSeriesElement, ...cleanConfigurations),
		disclaimersProps: getChartDisclaimersProps(...cleanConfigurations),
		breakpoints: getChartBreakpoints(defaultSeriesElement, ...cleanConfigurations),
		rebaseOptions: getRebaseOptions(...cleanConfigurations),
		dateOptions: getDateOptions(...cleanConfigurations),
		selectedChartPeriod: getSelectedChartPeriod(...cleanConfigurations),
		chartBaseComponent:
			(cleanConfigurations &&
				cleanConfigurations.length > 0 &&
				cleanConfigurations[cleanConfigurations.length - 1].chartBaseComponent) ||
			'ComposedChart',
	};
	return response;
}

export function getHighLowChartProps(props: IScatterChartProps): IScatterChartProps {
	let returnProps = props;
	let rawData: any[] | undefined = (props.composedChartProps && props.composedChartProps.data) || [];
	if (rawData.length === 0) {
		rawData = props.data;
	}

	if (rawData && props.highLowBubbleChartProps) {
		const {
			xDatakey,
			yDatakey,
			zDatakey,
			xLabel,
			yLabel,
			zLabel,
			highLowKey,
			highLowValue,
			lowColors,
			highColors,
			midBubbleBorderColor,
			midBubbleBorderWeight,
			midBubbleColor,
			minDotSize,
			maxDotSize,
			showDataInLegend,
			legendDataFormatter,
			sortOptions,
		} = props.highLowBubbleChartProps;

		const bubbleSortOrder = (sortOptions && sortOptions.sortOrder) || 'descending';
		const sortDataKey = (sortOptions && sortOptions.dataKey) || yDatakey;
		const highLowSortKey = highLowKey || yDatakey;

		let key = '';
		let value: any = '';
		let highlightFillColor = '';

		if (props.highlightDataPointProps && props.highlightDataPointProps.key) {
			key = props.highlightDataPointProps.key;
			value = props.highlightDataPointProps.value;
		}

		if (props.highlightDataPointProps && props.highlightDataPointProps.fillColor) {
			highlightFillColor = props.highlightDataPointProps.fillColor;
		}

		let highlightPoint = rawData.find((datapoint) => datapoint[key] === value);
		const data = rawData.sort((a, b) => (b[highLowSortKey] > a[highLowSortKey] ? 1 : -1));

		if (highlightPoint === undefined && props.highlightDataPointProps) {
			highlightPoint = data[data.length - 1];
		}

		const highValues: any[] = [];
		const lowValues: any[] = [];
		let seriesData: any[] = [];
		let max = 0;
		let min = 0;

		for (let i = 0; i < data.length; i++) {
			const item = data[i];
			const value = item[zDatakey];
			// Check if value is in the high values
			if (i < highLowValue) {
				highValues.push(item);
			}
			// Check if value is in the low values
			else if (i >= data.length - highLowValue) {
				lowValues.push(item);
			}
			// Start Loading up the Series Data, Mid Values will populate series data first to be rendered first (below) other scatter points
			else {
				seriesData.push(item);
			}
			if (i === 0) {
				min = value;
				max = value;
			} else {
				min = Math.min(min, value);
				max = Math.max(max, value);
			}
		}

		seriesData.push(...lowValues, ...highValues);

		seriesData =
			bubbleSortOrder === 'descending'
				? seriesData.sort((a, b) => (b[sortDataKey] > a[sortDataKey] ? 1 : -1))
				: seriesData.sort((a, b) => (a[sortDataKey] > b[sortDataKey] ? 1 : -1));

		const sizeRange = maxDotSize - minDotSize;
		const valueRange = max - min;
		const sizeValueRatio = sizeRange / valueRange;

		const seriesProps = seriesData.map((datapoint) => {
			const size = datapoint[zDatakey] * sizeValueRatio + minDotSize;

			if (lowValues.includes(datapoint)) {
				const index = lowValues.findIndex((item) => {
					return item === datapoint;
				});
				return {
					data: [datapoint],
					name: datapoint.label,
					fill: lowColors[index],
					shape: <CustomizedShape radius={size} borderColor={'#cccccc'} borderWeight={1} />,
				};
			}
			if (highValues.includes(datapoint)) {
				const index = highValues.findIndex((item) => {
					return item === datapoint;
				});
				return {
					data: [datapoint],
					name: datapoint.label,
					fill: highColors[index],
					shape: <CustomizedShape radius={size} borderColor={'#cccccc'} borderWeight={1} />,
				};
			}
			return {
				data: [datapoint],
				name: datapoint.label,
				fill: midBubbleColor || '#ffffff',
				shape: (
					<CustomizedShape
						radius={size}
						borderColor={midBubbleBorderColor || '#cccccc'}
						borderWeight={midBubbleBorderWeight || 1}
					/>
				),
			};
		});

		const minX = data.reduce((acc, datapoint, index) => {
			if (index === 0) {
				return datapoint[xDatakey];
			}
			return Math.min(datapoint[xDatakey], acc);
		}, 0);

		const maxX = data.reduce((acc, datapoint, index) => {
			if (index === 0) {
				return datapoint[xDatakey];
			}
			return Math.max(datapoint[xDatakey], acc);
		}, 0);

		const minY = data.reduce((acc, datapoint, index) => {
			if (index === 0) {
				return datapoint[yDatakey];
			}
			return Math.min(datapoint[yDatakey], acc);
		}, 0);

		const maxY = data.reduce((acc, datapoint, index) => {
			if (index === 0) {
				return datapoint[yDatakey];
			}
			return Math.max(datapoint[yDatakey], acc);
		}, 0);

		const minXDomain = Math.floor(minX);
		const maxXDomain = Math.ceil(maxX);
		const minYDomain = Math.floor(minY);
		const maxYDomain = Math.ceil(maxY);

		returnProps = {
			...returnProps,
			seriesProps,
			chartBaseComponent: 'ScatterChart',
			elementOverrides: {
				...props.elementOverrides,
				xAxis: {
					domain: [minXDomain, maxXDomain],
					...(props.elementOverrides && props.elementOverrides.xAxis),
					dataKey: xDatakey,
					type: 'number',
					name: xLabel,
					unit: '',
				},
				yAxis: {
					domain: [minYDomain, maxYDomain],
					...(props.elementOverrides && props.elementOverrides.yAxis),
					dataKey: yDatakey,
					type: 'number',
					name: yLabel,
					unit: '',
				},
				zAxis: {
					...(props.elementOverrides && props.elementOverrides.zAxis),
					dataKey: zDatakey,
					type: 'number',
					name: zLabel,
					unit: '',
				},
				legend: {
					...(props.elementOverrides && props.elementOverrides.legend),
					payload: [],
				},
			},
		};

		if (
			returnProps &&
			returnProps.elementOverrides &&
			returnProps.elementOverrides.legend &&
			returnProps.elementOverrides.legend.payload
		) {
			const payload = returnProps.elementOverrides.legend.payload;

			highValues.forEach((datapoint, index) => {
				let label = datapoint.label;
				let color = highColors[index];

				if (showDataInLegend) {
					let dataValue = datapoint[yDatakey];
					if (legendDataFormatter) {
						dataValue = common.commonUtils.formatter(legendDataFormatter, dataValue);
					}
					label = `${datapoint.label}: ${dataValue}`;
				}
				if (highlightPoint === datapoint) {
					color = highlightFillColor;
				}
				payload.push({
					color,
					value: label,
					id: `${datapoint.label}_${index}`,
					type: 'circle',
				});
			});
			lowValues.forEach((datapoint, index) => {
				let label = datapoint.label;
				let color = lowColors[index];
				if (showDataInLegend) {
					let dataValue = datapoint[yDatakey];
					if (legendDataFormatter) {
						dataValue = common.commonUtils.formatter(legendDataFormatter, dataValue);
					}
					label = `${datapoint.label}: ${dataValue}`;
				}
				if (highlightPoint === datapoint) {
					color = highlightFillColor;
				}
				payload.push({
					color,
					value: label,
					id: `${datapoint.label}_${index}`,
					type: 'circle',
				});
			});
		}
	}
	return returnProps;
}

export function highlightDataProps(props: IScatterChartProps): IScatterChartProps {
	const returnProps = props;
	const seriesProps: any[] | undefined = props.seriesProps || [];

	if (seriesProps.length > 0 && props.highlightDataPointProps) {
		const { key, value, fillColor, size, borderColor, borderWeight } = props.highlightDataPointProps;
		let index = seriesProps.length - 1;
		if (key) {
			// Gets the index of the highlighted data point for styles to be applied on
			index = seriesProps.findIndex((datapoint) => datapoint.data[0][key] === value);
		}

		if (index !== -1) {
			seriesProps[index] = {
				...seriesProps[index],
				name: seriesProps[index].label,
				fill: fillColor,
				shape: <CustomizedShape radius={size} borderColor={borderColor} borderWeight={borderWeight || 1} />,
			};
		}
	}

	return returnProps;
}

export function getChartSyncTicks(
	chartProps: ISingleChart<any>,
	elements: TChartElement[],
	composedChartProps: TComposedChartProps,
): { ticks: any[]; tickCount: number } | undefined {
	// Used to allow the x and y axis to have the same ticks
	if (chartProps.chartBaseComponent === 'ScatterChart' && (chartProps as IScatterChartProps).syncXYTicks) {
		const axisElements = elements.reduce(
			(acc: Map<'XAxis' | 'YAxis', TChartElementXAxis | TChartElementYAxis>, element: TChartElement) => {
				if (element.type === 'XAxis' || element.type === 'YAxis') {
					// Get the ticks for the series
					applyTickPivotPoint(element, elements, composedChartProps);
					acc.set(element.type, element);
				}
				return acc;
			},
			new Map(),
		);

		let tickFormatter: any;
		let tickPivotPoint: any;
		let centerTickPivotPoint: any;
		const combinedTicks: any[] = [];

		const xAxisElement: TChartElementXAxis | undefined = axisElements.get('XAxis') as TChartElementXAxis;
		if (xAxisElement && xAxisElement.props && xAxisElement.props.ticks) {
			const {
				tickFormatter: xTickFormatter,
				tickPivotPoint: xTickPivotPoint,
				centerTickPivotPoint: xCenterTickPivotPoint,
				ticks,
			} = xAxisElement.props as IXAxisProps;
			tickFormatter = xTickFormatter;
			tickPivotPoint = xTickPivotPoint;
			centerTickPivotPoint = xCenterTickPivotPoint;
			if (ticks) {
				combinedTicks.push(...ticks);
			}
		}

		const yAxisElement: TChartElementYAxis | undefined = axisElements.get('YAxis') as TChartElementYAxis;
		if (yAxisElement && yAxisElement.props && yAxisElement.props.ticks) {
			const {
				tickFormatter: yTickFormatter,
				tickPivotPoint: yTickPivotPoint,
				centerTickPivotPoint: yCenterTickPivotPoint,
				ticks,
			} = yAxisElement.props as IYAxisProps;

			// If not set by xAxis, then set the following
			if (!tickFormatter) {
				tickFormatter = yTickFormatter;
			}
			if (!tickPivotPoint) {
				tickPivotPoint = yTickPivotPoint;
			}
			if (!centerTickPivotPoint) {
				centerTickPivotPoint = yCenterTickPivotPoint;
			}
			// ********************************************
			if (ticks) {
				combinedTicks.push(...ticks);
			}
		}

		const precision = getTickPrecision(tickFormatter);
		const combinedTickCount = combinedTicks.length;
		const min = Math.min(...combinedTicks);
		const max = Math.max(...combinedTicks);
		const tickHandler = new TickHandler({ precision, count: combinedTickCount });
		// Work out the new combined ticks around the point
		const ticksAroundPoint = tickHandler.getTicksAroundPoint(
			tickPivotPoint === undefined ? 1 : tickPivotPoint,
			max,
			min,
			centerTickPivotPoint,
		);
		return {
			ticks: ticksAroundPoint,
			tickCount: combinedTickCount,
		};
	}
}

const seriesHasData = (...configurations: ISingleChart<any>[]) => {
	for (const configuration of [...configurations].reverse()) {
		const seriesProps = configuration.seriesProps;
		const data = configuration.data;
		if (seriesProps) {
			for (const seriesProp of seriesProps) {
				const dataKey = seriesProp.dataKey;
				let hasData = false;
				let length = 0;
				let count = 0;
				// Scatter charts dont have a data key on the series but on the axis
				if (dataKey) {
					if (data) {
						length = data.length;
						count = data.filter(
							(dataPoint) =>
								Object.prototype.hasOwnProperty.call(dataPoint, dataKey) &&
								dataPoint[dataKey] !== undefined &&
								dataPoint[dataKey] !== null,
						).length;
						for (const dataPoint of data) {
							if (Object.prototype.hasOwnProperty.call(dataPoint, dataKey) && dataPoint[dataKey]) {
								hasData = true;
								break;
							}
						}
					}
				} else {
					hasData = true;
				}
				Object.assign(seriesProp, { hasData, count, length });
			}
		}
	}
};

export function getRebaseOptions(...configurations: ISingleChart<any>[]) {
	let options;
	for (const configuration of [...configurations].reverse()) {
		const { rebaseOptions, seriesProps = [] } = configuration;
		if (rebaseOptions) {
			const seriesToRemove = getRebaseSeriesToRemove(seriesProps);
			for (const key of seriesToRemove) {
				removeRebaseOptions(key, rebaseOptions);
			}
			options = rebaseOptions;
		}
	}
	return options;
}

const getRebaseSeriesToRemove = (seriesProps: Partial<any>[]): string[] => {
	const series: string[] = [];
	if (seriesProps) {
		seriesProps.forEach((serie) => {
			if (!serie.hasData) {
				series.push(serie.dataKey);
			}
		});
	}
	return series;
};

const removeRebaseOptions = (key: string, options: IRebaseOptions) => {
	const series = options.series;
	if (series) {
		const idx = series.findIndex((elem) => elem.key === key);
		if (idx !== -1) {
			series.splice(idx, 1);
		}
	}
};

export function getDateOptions(...configurations: ISingleChart<any>[]) {
	for (const configuration of [...configurations].reverse()) {
		const { dateOptions } = configuration;
		if (dateOptions) {
			return dateOptions;
		}
	}
}

export function getSelectedChartPeriod(...configurations: ISingleChart<any>[]) {
	for (const configuration of [...configurations].reverse()) {
		const { selectedChartPeriod } = configuration;
		if (selectedChartPeriod) {
			return selectedChartPeriod;
		}
	}
}

export function getChartBreakpoints(
	defaultSeriesElement: TChartElement,
	...configurations: ISingleChart<any>[]
): IBreakpointProps<ISingleChart<any>>[] | undefined {
	for (const configuration of [...configurations].reverse()) {
		const { breakpoints } = configuration;
		if (breakpoints) {
			const props = configurations[configurations.length - 1];
			return breakpoints.map((breakpoint) => {
				const { modifier, size, props: propsFormBreakpoint } = breakpoint;
				const breakpointProps = common.commonUtils.deepMergeObjects(props, propsFormBreakpoint);
				const cleanConfigurations = [...configurations, breakpointProps].map((configuration) => {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { breakpoints, ...otherProps } = configuration;
					return otherProps;
				});
				return {
					size,
					modifier,
					props: getChartBaseProps(defaultSeriesElement, ...cleanConfigurations),
				};
			});
		}
	}
}

export function applyLabelQueries(
	element: TChartElement,
	translationContext: ITranslationContextValue,
	queryStore?: QueryStore,
) {
	if (element.type === 'XAxis' || element.type === 'YAxis') {
		if (element.props && element.props.labelQuery && queryStore) {
			const { labelQuery, label } = element.props;
			const labelValue = queryStore.query(labelQuery, translationContext);
			if (typeof label === 'object') {
				label.value = labelValue;
			} else {
				element.props.label = labelValue;
			}
		}
	}
}

export function applyFormatters(
	element: TChartElement,
	translationContext: ITranslationContextValue,
	queryStore?: QueryStore,
) {
	// Convert the tickFormatter from IFormatter to a function
	if (
		element.type === 'Brush' ||
		element.type === 'CartesianAxis' ||
		element.type === 'PolarAngleAxis' ||
		element.type === 'PolarRadiusAxis' ||
		element.type === 'XAxis' ||
		element.type === 'YAxis'
	) {
		if (element.props) {
			const formatterConfiguration = element.props.tickFormatter;
			if (formatterConfiguration && typeof formatterConfiguration === 'object') {
				element.props = Object.assign({}, element.props, {
					tickFormatter: (value: any) => {
						if (!formatterConfiguration.locale) {
							formatterConfiguration.locale = translationContext && translationContext.culture;
						}
						if (!formatterConfiguration.options) {
							formatterConfiguration.options = {};
						}
						if (queryStore && formatterConfiguration.currencyQuery) {
							formatterConfiguration.currency = queryStore.query(formatterConfiguration.currencyQuery);
						}
						// Translate the Threshold Text
						if (formatterConfiguration.options.numberThresholds) {
							formatterConfiguration.options.numberThresholds.forEach((numThreshold) => {
								numThreshold.text = translationContext.translate(numThreshold.text);
							});
						}
						return common.commonUtils.formatter(formatterConfiguration as IFormatter, value);
					},
				});
			}
		}
	}
	if (element.type === 'Tooltip' || element.type === 'Pie' || element.type === 'RadialBar') {
		if (element.props) {
			const formatterConfiguration = element.props.formatter;
			if (formatterConfiguration && typeof formatterConfiguration === 'object') {
				element.props = Object.assign({}, element.props, {
					formatter: (value: any) => {
						if (!formatterConfiguration.locale) {
							formatterConfiguration.locale = translationContext && translationContext.culture;
						}
						if (!formatterConfiguration.options) {
							formatterConfiguration.options = {};
						}
						if (queryStore && formatterConfiguration.currencyQuery) {
							formatterConfiguration.currency = queryStore.query(formatterConfiguration.currencyQuery);
						}
						return common.commonUtils.formatter(formatterConfiguration as IFormatter, value);
					},
				});
			}
			const labelFormatterConfiguration = element.props.labelFormatter;
			if (labelFormatterConfiguration && typeof labelFormatterConfiguration === 'object') {
				element.props = Object.assign({}, element.props, {
					labelFormatter: (value: any) => {
						if (!labelFormatterConfiguration.locale) {
							labelFormatterConfiguration.locale = translationContext && translationContext.culture;
						}
						if (!labelFormatterConfiguration.options) {
							labelFormatterConfiguration.options = {};
						}
						return common.commonUtils.formatter(labelFormatterConfiguration as IFormatter, value);
					},
				});
			}
		}
	}
}

export function getDynamicReferenceLines(
	elements: TChartElement[],
	composedChartProps: TComposedChartProps,
	configurations: ISingleChart<any>[] | undefined,
): TChartElement[] {
	const returnElements: TChartElement[] = [];
	const workingElements = [...elements];
	const xAxis = workingElements.find((ele) => ele.type === 'XAxis');
	const yAxis = workingElements.find((ele) => ele.type === 'YAxis');
	let xAxisTicks = [];
	let yAxisTicks = [];
	if (xAxis) {
		xAxisTicks = getTicks(xAxis, workingElements, composedChartProps) || [];
	}
	if (yAxis) {
		yAxisTicks = getTicks(yAxis, workingElements, composedChartProps) || [];
	}

	if (configurations && xAxisTicks && xAxisTicks.length > 0 && yAxisTicks && yAxisTicks.length > 0) {
		let YAxisMin = Math.max(...yAxisTicks);
		let YAxisMax = Math.min(...yAxisTicks);
		let XAxisMin = Math.max(...xAxisTicks);
		let XAxisMax = Math.min(...xAxisTicks);

		for (const configuration of configurations.reverse()) {
			const { ascendingDiagonalReferenceLine, descendingDiagonalReferenceLine } = configuration;
			const { syncXYTicks } = configuration as IScatterChartProps;
			if (syncXYTicks) {
				const combinedTicks = [...xAxisTicks, ...yAxisTicks];
				const combinedMin = Math.min(...combinedTicks);
				const combinedMax = Math.max(...combinedTicks);
				YAxisMin = XAxisMin = combinedMin;
				YAxisMax = XAxisMax = combinedMax;
			}
			if (ascendingDiagonalReferenceLine) {
				returnElements.push({
					type: 'ReferenceLine',
					props: {
						segment: [
							{ x: XAxisMin, y: YAxisMin },
							{ x: XAxisMax, y: YAxisMax },
						],
						...ascendingDiagonalReferenceLine,
					},
				});
			}
			if (descendingDiagonalReferenceLine) {
				returnElements.push({
					type: 'ReferenceLine',
					props: {
						segment: [
							{ x: XAxisMin, y: YAxisMax },
							{ x: XAxisMax, y: YAxisMin },
						],
						...descendingDiagonalReferenceLine,
					},
				});
			}
		}
	}
	return returnElements;
}

function getTicks(
	element: TChartElement,
	elements: TChartElement[],
	composedChartProps: TComposedChartProps,
): any[] | undefined {
	if ((element.type === 'YAxis' || element.type === 'XAxis') && element.props) {
		const { tickPivotPoint, tickCount, tickFormatter, dataKey, centerTickPivotPoint, persistQuadrants } =
			element.props;
		const barElement = elements.find((element) => element.type === 'Bar') as TChartElementBar;
		const labelListProps = barElement && barElement.props && barElement.props.labelList;
		const usingTopBarLabels = labelListProps && labelListProps.position && labelListProps.position === 'top';
		let ticks: any[] | undefined = [];

		const precision = getTickPrecision(tickFormatter);
		if (typeof tickPivotPoint === 'number') {
			const tickHandler = new TickHandler({
				precision,
				usingTopBarLabels,
				count: tickCount,
				elementProps: element.props,
			});
			ticks = tickHandler.getTicksForSeries(
				composedChartProps.data,
				getSeriesKeysFromChartElements(elements, (dataKey && dataKey.toString()) || undefined),
				tickPivotPoint,
				centerTickPivotPoint,
				persistQuadrants,
			);
			return ticks;
		}
	}
}

function getTickPrecision(tickFormatter: Partial<IFormatter> | TTickFormatterFunction | undefined): number | undefined {
	if (tickFormatter && typeof tickFormatter !== 'function' && tickFormatter.options) {
		const { maximumFractionDigits, minimumFractionDigits } = tickFormatter.options;
		return maximumFractionDigits || minimumFractionDigits;
	}
}

export function applyTickPivotPoint(
	element: TChartElement,
	elements: TChartElement[],
	composedChartProps: TComposedChartProps,
) {
	if ((element.type === 'YAxis' || element.type === 'XAxis') && element.props) {
		element.props.ticks = getTicks(element, elements, composedChartProps);
		// Need to have at least 3 ticks when explicitly setting the tickCount on an axis otherwise
		// recharts throws a max callstack error
		if (element && element.props && element.props.ticks && element.props.ticks.length > 2) {
			element.props.tickCount = element.props.ticks.length;
		}
	}
}

export function applyCustomTickLabel(element: TChartElement) {
	if ((element.type === 'YAxis' || element.type === 'XAxis') && element.props) {
		const { customTickLabel, tickFormatter, tick } = element.props;
		if (customTickLabel) {
			element.props.tick = (props: any) => {
				const { dy, dx, rotation, textAnchor, fill, width } = customTickLabel;
				const { x, y, payload } = props;
				const textProps: TextProps = {
					...tick,
					dy,
					dx,
					fill,
					width,
					textAnchor,
					angle: rotation,
					x: 0,
					y: 0,
				};

				let value = payload.value;
				if (tickFormatter && typeof tickFormatter === 'function') {
					value = tickFormatter(value);
				}
				return (
					<g transform={`translate(${x},${y})`}>
						<Text {...(textProps as any)}>{value}</Text>
					</g>
				);
			};
		}
	}
}

export function applySeriesLabels(element: TChartElement, translationContext: ITranslationContextValue) {
	if (element.type === 'Bar' && element.props) {
		const { labelList } = element.props;
		if (labelList && (labelList.position === 'outside' || labelList.position === 'end')) {
			element.props.label = customisedLabel;
			element.props.label.formatter = labelList.formatter;
			element.props.labelList = undefined;
		}
	}
	if (element.type === 'Line' && element.props) {
		const { lastLabel } = element.props;
		if (lastLabel && lastLabel.enabled) {
			element.props.label = (
				<LastSeriesLabel
					translationContext={translationContext}
					overrides={lastLabel}
					seriesProps={element.props}
				/>
			);
		}
	}
}

export function applyDataLength(element: TChartElement, dataLength: number | undefined) {
	if (element.type === 'Line' && element.props) {
		if (dataLength) {
			element.props.length = dataLength;
		}
	}
}

export function applyCustomTickValues(element: TChartElement, props: IChartBaseProps) {
	if ((element.type === 'YAxis' || element.type === 'XAxis') && element.props && props) {
		const { selectedChartPeriod, composedChartProps, dateOptions } = props;
		const tickOptions = getTickOptions(element.type, dateOptions);

		if (tickOptions) {
			let tickCount = getAxisTickCount(tickOptions, selectedChartPeriod);
			const syncTickOptions = getSyncTickOptionsForAxisType(element.type, dateOptions);

			const datasetCount = composedChartProps && composedChartProps.data && composedChartProps.data.length;
			if (datasetCount) {
				// Don't adjust the data set count if the syncTickOptions is configured,
				// it throws the calculations out and will not generate a tick per data point
				const datasetLength = syncTickOptions ? datasetCount : datasetCount - 1;
				tickCount = Math.min(tickCount, datasetLength);
			}

			if (tickCount > 0) {
				const data = (composedChartProps && composedChartProps.data) || [];
				const { dataKey = 'date', domain = ['dataMin', 'dataMax'] } = element.props;
				const domainValues = getDomainValues(domain, dataKey, data);
				element.props.ticks = getCustomTicks(domainValues, tickCount, dataKey, data, syncTickOptions);
				// Ensure that the axis interval configuration is removed, else Recharts will override the synced ticks
				if (element.type === 'XAxis' && syncTickOptions && element.props) {
					delete element.props.interval;
				}
			}
		}
	}
}

function getSyncTickOptionsForAxisType(
	axisType: 'XAxis' | 'YAxis',
	dateOptions?: IDateOptions,
): ISyncTickOptions | undefined {
	if (dateOptions) {
		if (axisType === 'XAxis') {
			return dateOptions.xAxisTickOptions && dateOptions.xAxisTickOptions.syncTickOptions;
		}
		if (axisType === 'YAxis') {
			return dateOptions.yAxisTickOptions && dateOptions.yAxisTickOptions.syncTickOptions;
		}
	}
}

export function applyAnimationLogic(element: TChartElement, props: IChartBaseProps) {
	switch (element.type) {
		case 'Area':
		case 'Bar':
		case 'Line':
		case 'Scatter':
		case 'Pie':
		case 'Radar':
		case 'RadialBar':
		case 'Treemap':
		case 'Tooltip': {
			const globalAnimation = (global as any)[KSYS_CHART_ANIMATION_KEY];
			const elementPropsAnimation = element.props && element.props.isAnimationActive;
			const propsAnimation = (props as any)['isAnimationActive'];

			let isAnimationActive = true;
			if (typeof elementPropsAnimation === 'boolean') {
				isAnimationActive = elementPropsAnimation;
			} else if (typeof propsAnimation === 'boolean') {
				isAnimationActive = propsAnimation;
			} else if (typeof globalAnimation === 'boolean') {
				isAnimationActive = globalAnimation;
			}

			if (element.props) {
				element.props.isAnimationActive = isAnimationActive;
			}

			break;
		}
		default:
			break;
	}
}

export function validateLegendElement(element: TChartElement, data: IChartData[] = []) {
	if (element.type === 'Line' || element.type === 'Area') {
		const { props } = element;
		if (props && props.legendType) {
			const { dataKey } = props;
			const validData = data.find((d) => !common.commonUtils.isNullOrEmpty(d[dataKey.toString()]));
			if (!validData) {
				props.legendType = 'none';
			}
		}
	}
}

export function getTickOptions(elementType: string, dateOptions?: IDateOptions): IAxisTickOptions | undefined {
	if (dateOptions) {
		if (elementType === 'XAxis' && dateOptions.xAxisDateAsNumber) {
			return dateOptions.xAxisTickOptions;
		}
		if (elementType === 'YAxis' && dateOptions.yAxisDateAsNumber) {
			return dateOptions.yAxisTickOptions;
		}
	}
}

export function getAxisTickCount(tickOptions: IAxisTickOptions, selectedPeriod?: string) {
	const { tickCount: defaultTickCount = 0, periodOptions = [] } = tickOptions;
	const periodTickCount = periodOptions.filter((option) => option.periodKey === selectedPeriod);
	if (!common.commonUtils.isNullOrEmpty(periodTickCount)) {
		return periodTickCount[0].tickCount || defaultTickCount;
	}
	return defaultTickCount;
}

export function getDomainValues(domain: [TAxisDomain, TAxisDomain] | string[], dataKey: TDataKey, data: object[]) {
	let [start, end] = domain;
	if (start === 'dataMin') {
		start = getValueWithDataKey(data[0], dataKey);
	}
	if (end === 'dataMax') {
		end = getValueWithDataKey(data[data.length - 1], dataKey);
	}
	return { start, end };
}

export function getValueWithDataKey(dataObject: any, dataKey: TDataKey) {
	if (typeof dataKey === 'string' || typeof dataKey === 'number') {
		return dataObject[dataKey];
	}
	return dataKey(dataObject);
}

export function getCustomTicks(
	domainValues: any,
	tickCount: number,
	dataKey: TDataKey = 'date',
	data: IChartData[],
	syncTickOptions?: ISyncTickOptions,
) {
	const { start, end } = domainValues;
	const interval = (end - start) / tickCount;
	const dataPoints = data
		.map((item) => getValueWithDataKey(item, dataKey))
		.filter((item) => item >= start && item <= end);

	if (syncTickOptions) {
		const tickHandler = new TickHandler({ syncTickOptions });
		const syncedTicks = tickHandler.getSyncedTicksFromData(dataPoints, tickCount);
		return syncedTicks;
	}

	const ticks: number[] = [start];

	for (let i = 1; i <= tickCount - 1; i++) {
		const target = start + interval * i;
		ticks.push(getClosestValue(target, dataPoints));
	}
	ticks.push(end);

	return ticks;
}

export function getClosestValue(target: number, values: number[]) {
	if (!common.commonUtils.isNullOrEmpty(values)) {
		const under = Math.max(...values.filter((value) => value <= target));
		const over = Math.min(...values.filter((value) => value >= target));

		if (target - under < over - target) {
			return under;
		}
		return over;
	}
	return target;
}

const customisedLabel = (passedProps: any) => {
	const { content, value } = passedProps;
	const labelFormatter = content && content.formatter;

	let xNew;
	let textAnchor;
	const fontHeight = 7;
	// messy formula to work out the central position of the label relative to the bar height
	const yPosition = passedProps.y + passedProps.height - 1 - (passedProps.height - fontHeight) / 2;

	if (passedProps.value < 0) {
		xNew = -2;
		textAnchor = 'end';
	} else {
		xNew = +2;
		textAnchor = 'starts';
	}

	return (
		<CustomSeriesLabel
			x={passedProps.x}
			y={yPosition}
			width={passedProps.width || 0}
			dx={(passedProps.width || 0) + xNew}
			fill={'#000000'}
			textAnchor={textAnchor}
		>
			{labelFormatter ? common.commonUtils.formatter(labelFormatter, value) : value}
		</CustomSeriesLabel>
	);
};

export function applyTranslationsToComposedChartProps(
	composedChartProps: TComposedChartProps,
	translationContext: ITranslationContextValue,
) {
	if (composedChartProps) {
		const { data } = composedChartProps;
		applyTranslationsToDataName(translationContext, data);
	}
}

export function applyTranslationsToDataName(translationContext: ITranslationContextValue, data?: IChartData[]) {
	if (data) {
		for (const row of data as any[]) {
			row['name'] = translationContext.translate(row.name);
		}
	}
}

export function applyRebaseToComposedChartProps(
	composedChartProps: TComposedChartProps,
	options: IRebaseOptions | undefined,
) {
	if (composedChartProps && !common.commonUtils.isNullOrEmpty(composedChartProps.data) && options) {
		const { data } = composedChartProps;
		composedChartProps.data = rebase(data, options);
	}
}

export function translateTickValues(translationContext: ITranslationContextValue) {
	return (tick: any) => {
		if (typeof tick === 'string') {
			return translationContext.translate(tick);
		}
		return tick;
	};
}

export function applyTranslations(element: TChartElement, translationContext: ITranslationContextValue) {
	// Convert the tickFormatter from IFormatter to a function
	if (element.type === 'XAxis' || element.type === 'YAxis') {
		if (element.props) {
			const label = element.props.label;
			if (label) {
				if (typeof label === 'object' && label.value) {
					label.value = translationContext.translate(label.value.toString());
				}
				if (typeof label === 'string') {
					element.props.label = translationContext.translate(label);
				}
			}

			// Translate the axis ticks
			// element.props = { ...element.props };
			if (!element.props.tickFormatter) {
				element.props.tickFormatter = translateTickValues(translationContext);
			}
		}
	}

	// Translate the chart element for static legend names
	if (element.type === 'Bar' || element.type === 'Line' || element.type === 'Area') {
		if (element.props) {
			const name = element.props.name;
			if (name) {
				if (typeof name === 'string') {
					element.props.name = translationContext.translate(name);
				}
			}
		}
	}
	if (element.type === 'Pie') {
		if (element.props && element.props.data) {
			for (const dataPoint of element.props.data) {
				dataPoint.name = translationContext.translate(dataPoint.name);
			}
		}
	}
}

export function getSeriesKeysFromChartElements(elements: TChartElement[], scatterKey?: string) {
	const response: string[] = [];
	for (const element of elements) {
		if (element.type === 'Area' || element.type === 'Bar' || element.type === 'Line') {
			if (element.props && element.props.dataKey) {
				response.push(element.props.dataKey.toString());
			}
		} else if (element.type === 'Scatter' && scatterKey) {
			response.push(scatterKey);
		}
	}
	return response;
}

export function getSeriesKey(row: object, rowIndex: number) {
	return `series_${rowIndex}`;
}

export function getSeriesKeysFromRows(rows: object[]) {
	return rows.map(getSeriesKey);
}

export function getValueColumns(columns: ITableColumn[] = []): ITableColumn[] {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [nameColumn, ...valueColumns] = columns;
	return valueColumns;
}

export function getNameColumn(columns: ITableColumn[] = []): ITableColumn {
	const [nameColumn] = columns;
	return nameColumn;
}

export function getChartDataFromTable(
	tableProps: ITableProps,
	globalConfiguration: IGlobalConfiguration | undefined,
	translationHelper?: common.helpers.TranslationHelper,
) {
	const { rows = [] } = tableProps;
	const tableLogic = new TableLogic(tableProps, globalConfiguration, translationHelper);
	const columns = tableLogic.visibleColumns;
	const valueColumns = getValueColumns(columns);
	const response: object[] = [];
	for (let valueColumnIndex = 0; valueColumnIndex < valueColumns.length; valueColumnIndex++) {
		const valueColumn = valueColumns[valueColumnIndex];
		const { header } = valueColumn;
		if (valueColumnIndex > response.length - 1) {
			response.push({
				name: header || 'No Name',
			});
		}
		const responseRow = response[valueColumnIndex];
		rows.forEach((row, rowIndex) => {
			const key = getSeriesKey(row, rowIndex);
			(responseRow as any)[key] = tableLogic.getRawValue(row, valueColumn);
		});
	}

	return response;
}

export function rebase(data: any[], options: IRebaseOptions) {
	if (!common.commonUtils.isNullOrEmpty(data) && options) {
		const rebaser = new common.helpers.RebaseHelper({ transformClass: query.transform.Transform }, options);
		return rebaser.rebase(data);
	}
	return data;
}

export function getNumber(property: any): number | undefined {
	if (typeof property === 'number') {
		return property;
	}
	if (typeof property === 'string') {
		return parseInt(property, 10);
	}
	return;
}

export function calculateBarChartHeight(
	composedChartProps: TComposedChartProps = {},
	elements: TChartElement[] = [],
): number | undefined {
	if (composedChartProps.layout !== 'vertical') {
		return;
	}

	const barElements = elements.filter((element) => element.type === 'Bar') as TChartElementBar[];
	if (barElements.length > 0) {
		const barElement = barElements[0];
		if (barElement && barElement.props) {
			// workout bar size
			const maxBarSize = getNumber(composedChartProps.maxBarSize);
			let barSize = getNumber(barElement.props.barSize || composedChartProps.barSize);
			if (maxBarSize && (!barSize || barSize > maxBarSize)) {
				barSize = maxBarSize;
			}

			// calculate chart height based on bar chart sizes
			const data = composedChartProps.data;
			if (barSize && Array.isArray(data) && data.length > 0) {
				const dataCount = data.length;
				const seriesCount = barElements.length;
				const barGap = getNumber(composedChartProps.barGap) || 0;

				/**
				 * Calculating category gap
				 * It appears recharts default to 10 when the specified values is 1 or lower.
				 */
				const barCategoryGapRaw = getNumber(composedChartProps.barCategoryGap);
				const barCategoryGap = barCategoryGapRaw && barCategoryGapRaw > 1 ? barCategoryGapRaw : 10;
				const categoryGaps = dataCount * barCategoryGap + barCategoryGap; // we add the additional gap for the gap that precedes the first bar.

				// Each bar in the category contributes to the height
				const barSizes = dataCount * seriesCount * barSize;

				// Each gap between the bars in a category contributes to the chart height
				const barGaps = dataCount * (seriesCount - 1) * barGap;

				// The xAxis height affects the chart height
				let xAxisHeight = 0;
				const xAxisElement = elements.find((element) => element.type === 'XAxis') as TChartElementXAxis;
				if (xAxisElement && xAxisElement.props) {
					xAxisHeight = xAxisElement.props.height || 30;
				}

				// The legend height affects the chart height
				let legendHeight = 0;
				const legendElement = elements.find((element) => element.type === 'Legend') as TChartElementLegend;
				if (legendElement && legendElement.props) {
					legendHeight = legendElement.props.height || 40;
				}

				return barSizes + barGaps + categoryGaps + xAxisHeight + legendHeight;
			}
		}
	}
	return;
}

export function useAxisDateAsNumber(
	axis: TSingleChartElement<IXAxisProps | IYAxisProps>,
	data?: IChartData[],
): IChartData[] {
	const response: IChartData[] = [];
	if (data) {
		if (axis.dataKey) {
			axis.type = 'number';
			for (const element of data) {
				const clone: IChartData = Object.assign({}, element);
				let value = clone[axis.dataKey as string];
				value = new Date(value).getTime();
				clone[axis.dataKey as string] = value;
				response.push(clone);
			}
		} else {
			response.push(...data);
		}
	}
	return response;
}

/**
 * Sets up the global disclaimers for the line and bars and returns a boolean to indicate if disclaimers are being used
 * @returns boolean
 */
export function applyGlobalDisclaimers(
	elements: TChartElement[],
	data: IChartData[] = [],
	queryStore: QueryStore | undefined,
	appKey: string | undefined,
): boolean {
	let hasSuperscriptDisclaimer = false;
	query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
	const globalDisclaimerStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
	if (globalDisclaimerStore) {
		let tooltipElement: TChartElement | null = null;
		const globalDisclaimers = new Map<string, any[]>();
		for (const element of elements) {
			// Grab the tooltip element so that we can add the global disclaimers to it's props later
			if (element.type === 'Tooltip') {
				tooltipElement = element;
			}
			// currently only supporting bars and lines
			if (element.type === 'Bar' || element.type === 'Line') {
				if (element && element.props && element.props.disclaimers !== undefined && queryStore && appKey) {
					hasSuperscriptDisclaimer = true;
					// Use any type to apply an internal _globalDisclaimers prop with the applicable disclaimers
					if (!(element.props as any)._globalDisclaimers) {
						(element.props as any)._globalDisclaimers = [];
					}
					for (const targettedDisclaimer of element.props.disclaimers) {
						const { disclaimers: disclaimersConfig = [], conditional } = targettedDisclaimer;
						if (conditional) {
							// TODO: need to figure out the first arg
							const conditionalHelper = new common.helpers.ConditionalHelper({}, conditional);
							if (
								conditionalHelper.matchesWithOptions({
									instance: data,
									executionOptions: { responseContext: data },
								})
							) {
								// pushes the current element disclaimers onto the props so that we can get the relevant superscripts
								const workingDisclaimers = globalDisclaimerStore.add(
									appKey,
									data,
									queryStore,
									...disclaimersConfig,
								);

								(element.props as any)._globalDisclaimers.push(...(workingDisclaimers as any[]));
								globalDisclaimers.set((element.props as any).dataKey, workingDisclaimers);
							}
						} else {
							// pushes the current element disclaimers onto the props so that we can get the relevant superscripts
							const workingDisclaimers = globalDisclaimerStore.add(
								appKey,
								data,
								queryStore,
								...disclaimersConfig,
							);
							(element.props as any)._globalDisclaimers.push(workingDisclaimers as any[]);
							globalDisclaimers.set((element.props as any).dataKey, workingDisclaimers);
						}
					}
				}
			}
		}
		if (tooltipElement?.props && globalDisclaimers) {
			(tooltipElement.props as any)._globalDisclaimers = globalDisclaimers;
		}
	}
	return hasSuperscriptDisclaimer;
}
